import { useEffect, useRef, useState, type ReactNode } from "react";

import type { ActivityResponse } from "@/hooks/useActivity";
import { clockTime, earlierRows, serverRunningRows, waitingRows, type ActivityRow } from "@/lib/activityRows";
import type { SessionActivity } from "@/stores/activity-store";

/** A running op from the bar, as the panel shows it. */
export interface LiveOp {
  type: string;
  icon: ReactNode;
  label: string;
  color: string;
  completed: number;
  total: number;
  uncounted?: boolean;
  countLabel?: string;
  detail: string | null;
  stats?: string | null;
  onCancel?: () => void;
  cancelPrompt?: string;
  /** A queued start (slice 1c): shown under Waiting; its cancel is "Remove". */
  waiting?: boolean;
  /** Server-backed op standing in for this store op type (e.g. "pdf"). */
  covers?: string;
}

interface Props {
  conceptName?: string;
  liveOps: LiveOp[];
  data: ActivityResponse | undefined;
  session: SessionActivity[];
  /** Op type a pill click asked for: scrolled to and highlighted. */
  focus: string | null;
  onCollapse: () => void;
  onShowMonitorPapers: () => void;
  /** Durable jobs (slice 1c): Earlier today comes from job_runs. */
  durable?: boolean;
  onRunAgain?: (runId: string) => void;
  onResume?: (runId: string) => void;
}

const EARLIER_SHOWN = 3;

const TONE: Record<ActivityRow["tone"], { mark: string; color: string }> = {
  running: { mark: "●", color: "#89b4fa" },
  waiting: { mark: "◷", color: "#f9e2af" },
  success: { mark: "✓", color: "#a6e3a1" },
  failed: { mark: "!", color: "#f38ba8" },
  cancelled: { mark: "■", color: "#6c7086" },
};

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="px-3 pb-0.5 pt-2 text-[10px] font-semibold tracking-wider text-slate-500">{title}</div>
      {children}
    </div>
  );
}

function LiveRow({ op, focused }: { op: LiveOp; focused: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [focused]);
  const percent = op.total > 0 ? Math.round((op.completed / op.total) * 100) : 0;
  const count = op.uncounted ? op.countLabel : `${op.completed} of ${op.total}`;
  const sub = [count, op.stats].filter(Boolean).join(" · ");
  const now = !op.detail
    ? null
    : op.waiting || /^(Now|Seed):|^(Stopping|Removing)…$/.test(op.detail) ? op.detail : `Now: ${op.detail}`;
  return (
    <div
      ref={ref}
      data-activity-row={op.type}
      className={`grid grid-cols-[16px_1fr_auto] items-start gap-2 border-b border-slate-700/60 px-3 py-1.5 ${focused ? "bg-slate-700/40" : ""}`}
    >
      <span className="mt-0.5 [&_svg]:size-3.5" style={{ color: op.color }}>{op.icon}</span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-slate-200">{op.label}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
        {now && <div className="truncate text-[11px] text-slate-500">{now}</div>}
        {!op.uncounted && op.total > 0 && (
          <div className="mt-1 h-0.5 overflow-hidden rounded-full bg-slate-700" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: op.color }} />
          </div>
        )}
        {confirming && op.onCancel && (
          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-300">
            <span>{op.cancelPrompt}</span>
            <button onClick={() => setConfirming(false)} className="rounded border border-slate-600 px-1.5 hover:bg-slate-700">
              Keep going
            </button>
            <button
              onClick={() => { setConfirming(false); op.onCancel?.(); }}
              className="rounded bg-red-400 px-1.5 font-medium text-slate-900 hover:bg-red-300"
            >
              Stop
            </button>
          </div>
        )}
      </div>
      {op.onCancel && !confirming && (
        <button
          onClick={() => (op.cancelPrompt ? setConfirming(true) : op.onCancel?.())}
          className="rounded bg-slate-700 px-2 py-0.5 text-[11px] text-slate-400 hover:text-slate-200"
        >
          {op.waiting ? "Remove" : "Cancel"}
        </button>
      )}
    </div>
  );
}

type OnAction = (a: NonNullable<ActivityRow["action"]>, runId?: string) => void;

function Row({ row, onAction }: { row: ActivityRow; onAction: OnAction }) {
  const tone = TONE[row.tone];
  const time = row.timeText ?? (row.at != null && row.tone !== "running" ? clockTime(row.at) : null);
  return (
    <div data-activity-row={row.key} className="grid grid-cols-[16px_1fr_auto] items-start gap-2 border-b border-slate-700/60 px-3 py-1.5">
      <span className="mt-px text-xs" style={{ color: tone.color }} aria-hidden>
        {tone.mark}
      </span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-slate-200">{row.title}</div>
        {row.sub && <div className="text-[11px] text-slate-400">{row.sub}</div>}
        {row.now && <div className="truncate text-[11px] text-slate-500">{row.now}</div>}
      </div>
      <div className="flex items-center gap-2 whitespace-nowrap">
        {row.action === "showMonitorPapers" && (
          <button onClick={() => onAction("showMonitorPapers")} className="text-[11px] text-sky-300 underline">
            Show them
          </button>
        )}
        {(row.action === "runAgain" || row.action === "resume") && (
          <button
            onClick={() => onAction(row.action!, row.runId)}
            className="rounded bg-sky-400 px-2 py-0.5 text-[11px] font-medium text-slate-900 hover:bg-sky-300"
          >
            {row.action === "resume" ? "Resume" : "Run again"}
          </button>
        )}
        {time && <span className="text-[10px] text-slate-500">{time}</span>}
      </div>
    </div>
  );
}

/** The bar's expanded state: what's running, what's waiting, what finished
 * today (Curate slice 1b). */
export function ActivityPanel({
  conceptName, liveOps: allLiveOps, data, session, focus, onCollapse, onShowMonitorPapers,
  durable = false, onRunAgain, onResume,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  // "checked 2 min ago" ages while the panel stays open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const liveOps = allLiveOps.filter((o) => !o.waiting);
  const waitingOps = allLiveOps.filter((o) => o.waiting);
  const liveTypes = new Set(liveOps.flatMap((o) => (o.covers ? [o.type, o.covers] : [o.type])));
  const running = serverRunningRows(data, (t) => liveTypes.has(t));
  const waiting = waitingRows(data);
  const earlier = earlierRows(data, session, now, durable);
  const shown = showAll ? earlier : earlier.slice(0, EARLIER_SHOWN);
  const onAction: OnAction = (a, runId) => {
    if (a === "showMonitorPapers") onShowMonitorPapers();
    else if (a === "runAgain" && runId) onRunAgain?.(runId);
    else if (a === "resume" && runId) onResume?.(runId);
  };
  const nothing =
    liveOps.length + waitingOps.length + running.length + waiting.length + earlier.length === 0;

  return (
    <section aria-label="Activity" className="max-h-[50vh] overflow-y-auto border-b border-slate-700 bg-slate-900/60">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-800 px-3 py-1.5">
        <span className="text-xs font-semibold text-slate-200">
          Activity{conceptName ? ` · ${conceptName}` : ""}
        </span>
        <button onClick={onCollapse} className="text-[11px] text-slate-500 hover:text-slate-300">
          Collapse ▾
        </button>
      </div>
      {(liveOps.length > 0 || running.length > 0) && (
        <Group title="RUNNING">
          {liveOps.map((op) => <LiveRow key={op.type} op={op} focused={focus === op.type} />)}
          {running.map((r) => <Row key={r.key} row={r} onAction={onAction} />)}
        </Group>
      )}
      {waiting.length + waitingOps.length > 0 && (
        <Group title="WAITING">
          {waitingOps.map((op) => <LiveRow key={op.type} op={op} focused={focus === op.type} />)}
          {waiting.map((r) => <Row key={r.key} row={r} onAction={onAction} />)}
        </Group>
      )}
      {earlier.length > 0 && (
        <Group title="EARLIER TODAY">
          {shown.map((r) => <Row key={r.key} row={r} onAction={onAction} />)}
          {earlier.length > EARLIER_SHOWN && (
            <button onClick={() => setShowAll((v) => !v)} className="px-3 py-1 text-[11px] text-sky-300 underline">
              {showAll ? "Show fewer" : `Show all (${earlier.length})`}
            </button>
          )}
        </Group>
      )}
      {nothing && <p className="px-3 py-3 text-xs text-slate-500">Nothing has run in this concept today.</p>}
      {!!data?.elsewhere_running && (
        <p className="px-3 py-1.5 text-[11px] text-slate-500">
          Work is running in {data.elsewhere_running} other concept{data.elsewhere_running === 1 ? "" : "s"}.
        </p>
      )}
    </section>
  );
}
